/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
const libUserMarkdown = require('./_UserMarkdown.js');

/**
 * A record's photo, kept as an image observation: the field's value is that observation's IDObservation (0 = no
 * photo). PictForm.PhotoEntity names the record's entity (e.g. Location), whose API routes own the photo:
 *
 *   POST {Entity}/{IDRecord}/Photo?FileName=...  uploads the image and links it to the record, answers { IDObservation }
 *   GET  {Entity}/{IDRecord}/Photo/Preview       the saved record's photo
 *
 * The observation is never read or written directly, and the upload needs a saved record. So Add photo / Replace
 * photo only holds the chosen image (shown from the browser) until the form saves; the record's save then calls
 * savePendingPhoto once it has an ID, which for a new record means after it is created. Remove photo drops a held
 * image and puts 0 in the field, which the save writes. Neither Replace nor Remove deletes the old observation.
 *
 * With PictForm.ReadOnly the photo shows without the buttons.
 */
class ObservationPhotoInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		/** @type {Record<string, { Entity: string, IDRecord: number }>} per field (by RawHTMLID): whose photo it is */
		this._fields = {};
		/** @type {Record<string, { File: File, PreviewURL: string }>} by photoKey: an image chosen here, waiting for the record to save */
		this._pendingPhotos = {};
		/** @type {Record<number, string>} IDObservation -> object URL of an image uploaded from here, shown while it is processed */
		this._localPreviews = {};
	}

	getInputHTMLID(pInputHTMLID)
	{
		return `#${pInputHTMLID}`;
	}

	getPreviewHTMLID(pInputHTMLID)
	{
		return `#PHOTO-PREVIEW-FOR-${pInputHTMLID}`;
	}

	getControlsHTMLID(pInputHTMLID)
	{
		return `#PHOTO-CONTROLS-FOR-${pInputHTMLID}`;
	}

	getFileInputHTMLID(pInputHTMLID)
	{
		return `#PHOTO-FILE-INPUT-FOR-${pInputHTMLID}`;
	}

	getAddLabelHTMLID(pInputHTMLID)
	{
		return `#PHOTO-ADD-LABEL-FOR-${pInputHTMLID}`;
	}

	getRemoveButtonHTMLID(pInputHTMLID)
	{
		return `#PHOTO-REMOVE-FOR-${pInputHTMLID}`;
	}

	getStatusHTMLID(pInputHTMLID)
	{
		return `#PHOTO-STATUS-FOR-${pInputHTMLID}`;
	}

	/**
	 * @param {any} pValue - a field value
	 * @return {number} the IDObservation it holds, or 0 when it holds none
	 */
	static idObservationOf(pValue)
	{
		const tmpID = Number(pValue);
		return (Number.isFinite(tmpID) && (tmpID > 0)) ? tmpID : 0;
	}

	/**
	 * @param {string} pSelector
	 * @return {HTMLElement|null} the first element the selector finds
	 */
	_element(pSelector)
	{
		const tmpElements = this.pict.ContentAssignment.getElement(pSelector);
		if (Array.isArray(tmpElements) && (tmpElements.length > 0) && (tmpElements[0] instanceof HTMLElement))
		{
			return tmpElements[0];
		}
		return null;
	}

	/**
	 * @param {string} pSelector
	 * @param {boolean} pShown
	 */
	_setShown(pSelector, pShown)
	{
		const tmpElement = this._element(pSelector);
		if (!tmpElement)
		{
			return;
		}
		if (pShown)
		{
			this.pict.ContentAssignment.removeClass(tmpElement, 'is-hidden');
		}
		else
		{
			this.pict.ContentAssignment.addClass(tmpElement, 'is-hidden');
		}
	}

	/**
	 * @param {string} pPath - an API path, e.g. Location/5/Photo/Preview
	 * @return {string} its full URL
	 */
	apiURL(pPath)
	{
		const tmpRestClient = this.pict.fable.services && this.pict.fable.services.HeadlightRestClient;
		if (tmpRestClient && (typeof tmpRestClient.formatUrl === 'function'))
		{
			try
			{
				return tmpRestClient.formatUrl(pPath);
			}
			catch (pError)
			{
				// fall through to the relative URL
			}
		}
		return `/1.0/${pPath}`;
	}

	/**
	 * @param {string} pEntity - e.g. Location
	 * @param {number} pIDRecord - the saved record; the route finds its photo from the record
	 * @return {string} the URL of the record's preview-sized photo
	 */
	photoURL(pEntity, pIDRecord)
	{
		return this.apiURL(`${encodeURIComponent(pEntity)}/${Number(pIDRecord)}/Photo/Preview`);
	}

	/**
	 * Remember whose photo a field holds: the entity, and the record's ID read from the field's sibling ID{Entity}
	 * (0 while the record is new).
	 *
	 * @param {Object} pInput
	 * @param {Object} [pView] - the form view, to read the record from
	 * @return {{ Entity: string, IDRecord: number }}
	 */
	rememberField(pInput, pView)
	{
		const tmpEntity = (pInput.PictForm && pInput.PictForm.PhotoEntity) || '';
		let tmpIDRecord = 0;
		const tmpAddress = String(pInput.Address || '');
		if (tmpEntity && pView && (typeof pView.getMarshalDestinationObject === 'function'))
		{
			const tmpSplit = tmpAddress.lastIndexOf('.');
			const tmpIDAddress = `${(tmpSplit >= 0) ? tmpAddress.substring(0, tmpSplit + 1) : ''}ID${tmpEntity}`;
			tmpIDRecord = ObservationPhotoInputHandler.idObservationOf(this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpIDAddress));
		}
		const tmpField = { Entity: tmpEntity, IDRecord: tmpIDRecord };
		this._fields[pInput.Macro.RawHTMLID] = tmpField;
		return tmpField;
	}

	/**
	 * Show a message under the photo, or clear it.
	 *
	 * @param {string} pInputHTMLID
	 * @param {string} [pMessage] - '' or omitted clears it
	 * @param {boolean} [pIsError]
	 */
	setStatus(pInputHTMLID, pMessage, pIsError)
	{
		const tmpStatus = this._element(this.getStatusHTMLID(pInputHTMLID));
		if (!tmpStatus)
		{
			return;
		}
		this.pict.ContentAssignment.assignContent(tmpStatus, libUserMarkdown.escapeHTML(pMessage || ''));
		if (pIsError)
		{
			this.pict.ContentAssignment.addClass(tmpStatus, 'is-danger');
		}
		else
		{
			this.pict.ContentAssignment.removeClass(tmpStatus, 'is-danger');
		}
		this._setShown(this.getStatusHTMLID(pInputHTMLID), !!pMessage);
	}

	/**
	 * @param {string} pEntity
	 * @param {number} pIDRecord - 0 for a record not yet created
	 * @return {string} the key a held image is kept under
	 */
	static photoKey(pEntity, pIDRecord)
	{
		return `${pEntity}:${Number(pIDRecord) || 0}`;
	}

	/**
	 * @param {File} pFile
	 * @return {string} an object URL showing the file, or '' when the browser cannot make one
	 */
	_previewURLFor(pFile)
	{
		try
		{
			return ((typeof URL !== 'undefined') && (typeof URL.createObjectURL === 'function')) ? URL.createObjectURL(pFile) : '';
		}
		catch (pError)
		{
			return '';
		}
	}

	/**
	 * Draw the field for a value: the photo (or "No photo." when read-only), and Add photo / Replace photo plus
	 * Remove photo when editable. An image waiting for the save shows in place of the saved one.
	 *
	 * @param {string} pInputHTMLID
	 * @param {any} pValue - the IDObservation, or 0
	 * @param {boolean} [pReadOnly]
	 */
	showPhoto(pInputHTMLID, pValue, pReadOnly)
	{
		const tmpIDObservation = ObservationPhotoInputHandler.idObservationOf(pValue);
		const tmpField = this._fields[pInputHTMLID] || { Entity: '', IDRecord: 0 };
		const tmpPending = pReadOnly ? null : this._pendingPhotos[ObservationPhotoInputHandler.photoKey(tmpField.Entity, tmpField.IDRecord)];
		const tmpHasPhoto = !!(tmpIDObservation || tmpPending);
		const tmpPreview = this._element(this.getPreviewHTMLID(pInputHTMLID));
		if (tmpPreview)
		{
			let tmpPreviewHTML = '';
			// a held or just-uploaded image shows from the browser; a saved one through the record's photo route
			let tmpSource = '';
			if (tmpPending)
			{
				tmpSource = tmpPending.PreviewURL;
			}
			else if (tmpIDObservation)
			{
				tmpSource = this._localPreviews[tmpIDObservation] ||
					((tmpField.Entity && tmpField.IDRecord) ? this.photoURL(tmpField.Entity, tmpField.IDRecord) : '');
			}
			if (tmpSource)
			{
				// A just-uploaded image can take a moment to process; say so instead of showing a broken image.
				tmpPreviewHTML = /*HTML*/`<img src="${libUserMarkdown.escapeHTML(tmpSource)}" alt="Photo" style="display: block; max-width: 100%; max-height: 240px; border-radius: 6px;" onerror="this.classList.add('is-hidden'); this.nextElementSibling.classList.remove('is-hidden');" />` +
					/*HTML*/`<p class="help is-hidden">The photo is saved. Its preview will show here once it has been processed.</p>`;
			}
			else if (tmpPending)
			{
				tmpPreviewHTML = /*HTML*/`<div class="input is-static has-text-text-60"><em>${libUserMarkdown.escapeHTML(tmpPending.File.name || 'Photo')} will be uploaded when you save.</em></div>`;
			}
			else if (pReadOnly && !tmpIDObservation)
			{
				tmpPreviewHTML = /*HTML*/`<div class="input is-static has-text-text-60"><em>No photo.</em></div>`;
			}
			this.pict.ContentAssignment.assignContent(tmpPreview, tmpPreviewHTML);
			this._setShown(this.getPreviewHTMLID(pInputHTMLID), !!tmpPreviewHTML);
		}
		this._setShown(this.getControlsHTMLID(pInputHTMLID), !pReadOnly);
		const tmpAddLabel = this._element(this.getAddLabelHTMLID(pInputHTMLID));
		if (tmpAddLabel)
		{
			this.pict.ContentAssignment.assignContent(tmpAddLabel, tmpHasPhoto ? 'Replace photo' : 'Add photo');
		}
		this._setShown(this.getRemoveButtonHTMLID(pInputHTMLID), tmpHasPhoto);
	}

	/**
	 * Put a value in the field's hidden input and tell the form it changed.
	 *
	 * @param {string} pInputHTMLID
	 * @param {number} pIDObservation - 0 for no photo
	 */
	setValue(pInputHTMLID, pIDObservation)
	{
		const tmpHidden = this._element(this.getInputHTMLID(pInputHTMLID));
		if (!(tmpHidden instanceof HTMLInputElement))
		{
			return;
		}
		this.pict.ContentAssignment.setAttribute(tmpHidden, 'value', String(pIDObservation));
		tmpHidden.value = String(pIDObservation);
		tmpHidden.dispatchEvent(new Event('change', { bubbles: true }));
	}

	/**
	 * Upload an image through the record's photo route, which creates the observation and links it to the record.
	 *
	 * @param {string} pEntity - e.g. Location
	 * @param {number} pIDRecord - the saved record
	 * @param {File} pFile
	 * @return {Promise<number>} the new IDObservation, or 0 when it could not be uploaded
	 */
	async uploadPhoto(pEntity, pIDRecord, pFile)
	{
		const tmpRestClient = this.pict.fable.services && this.pict.fable.services.HeadlightRestClient;
		const tmpIDRecord = ObservationPhotoInputHandler.idObservationOf(pIDRecord);
		if (!pEntity || !tmpIDRecord || !tmpRestClient || (typeof tmpRestClient.postBinary !== 'function'))
		{
			this.pict.log.error('ObservationPhoto: there is no saved record, PhotoEntity or HeadlightRestClient, so the photo cannot be uploaded.');
			return 0;
		}
		const tmpUploadTracking = this.pict.providers.UploadTracking;
		const tmpUploadID = tmpUploadTracking ? tmpUploadTracking.trackUpload() : null;
		try
		{
			const tmpBody = await new Promise((fResolve, fReject) =>
			{
				tmpRestClient.postBinary(`${encodeURIComponent(pEntity)}/${tmpIDRecord}/Photo?FileName=${encodeURIComponent(pFile.name || '')}`, pFile, pFile.type,
					(pError, pBody) => (pError ? fReject(pError) : fResolve(pBody)));
			});
			const tmpResult = (typeof tmpBody === 'string') ? JSON.parse(tmpBody) : tmpBody;
			if (tmpResult && tmpResult.Error)
			{
				throw new Error(tmpResult.Error.message || tmpResult.Error);
			}
			return ObservationPhotoInputHandler.idObservationOf(tmpResult && tmpResult.IDObservation);
		}
		catch (pError)
		{
			this.pict.log.error(`ObservationPhoto: the photo could not be uploaded: ${(pError && pError.message) || pError}`);
			return 0;
		}
		finally
		{
			if (tmpUploadTracking)
			{
				tmpUploadTracking.untrackUpload(tmpUploadID);
			}
		}
	}

	/**
	 * Called by the record's save once the record has an ID: upload the image held for it, if any. A new record's
	 * image is held under ID 0, so pass that as pHeldForIDRecord and the created ID as pIDRecord.
	 *
	 * @param {string} pEntity - e.g. Location
	 * @param {number} pHeldForIDRecord - the ID the form had when the image was chosen (0 for a new record)
	 * @param {number} pIDRecord - the saved record's ID
	 * @return {Promise<number|null>} the new IDObservation; 0 when the upload failed; null when no image was held
	 */
	async savePendingPhoto(pEntity, pHeldForIDRecord, pIDRecord)
	{
		const tmpKey = ObservationPhotoInputHandler.photoKey(pEntity, pHeldForIDRecord);
		const tmpPending = this._pendingPhotos[tmpKey];
		if (!tmpPending)
		{
			return null;
		}
		delete this._pendingPhotos[tmpKey];
		const tmpIDObservation = await this.uploadPhoto(pEntity, pIDRecord, tmpPending.File);
		if (tmpIDObservation && tmpPending.PreviewURL)
		{
			this._localPreviews[tmpIDObservation] = tmpPending.PreviewURL;
		}
		return tmpIDObservation;
	}

	/**
	 * Add photo / Replace photo: hold the chosen image until the record saves.
	 *
	 * @param {string} pInputHTMLID
	 * @return {Promise<boolean>} true when an image is now held
	 */
	async handlePhotoSelection(pInputHTMLID)
	{
		const tmpFileInput = this._element(this.getFileInputHTMLID(pInputHTMLID));
		if (!(tmpFileInput instanceof HTMLInputElement) || !tmpFileInput.files || (tmpFileInput.files.length < 1))
		{
			return false;
		}
		const tmpFile = tmpFileInput.files[0];
		// clear the chooser so picking the same file again still fires a change
		tmpFileInput.value = '';
		if (!/^image\//i.test(tmpFile.type || ''))
		{
			this.setStatus(pInputHTMLID, 'Choose an image file.', true);
			return false;
		}
		const tmpField = this._fields[pInputHTMLID] || { Entity: '', IDRecord: 0 };
		this._pendingPhotos[ObservationPhotoInputHandler.photoKey(tmpField.Entity, tmpField.IDRecord)] = { File: tmpFile, PreviewURL: this._previewURLFor(tmpFile) };
		this.setStatus(pInputHTMLID, 'The photo will be uploaded when you save.');
		// the value itself only changes when the upload links the photo; this marks the form as changed
		const tmpHidden = this._element(this.getInputHTMLID(pInputHTMLID));
		this.setValue(pInputHTMLID, ObservationPhotoInputHandler.idObservationOf(tmpHidden instanceof HTMLInputElement ? tmpHidden.value : 0));
		this.showPhoto(pInputHTMLID, tmpHidden instanceof HTMLInputElement ? tmpHidden.value : 0, false);
		return true;
	}

	/**
	 * Remove photo: drop any held image and unlink the photo from the record (the observation itself is kept).
	 *
	 * @param {string} pInputHTMLID
	 */
	handleRemovePhoto(pInputHTMLID)
	{
		const tmpField = this._fields[pInputHTMLID] || { Entity: '', IDRecord: 0 };
		delete this._pendingPhotos[ObservationPhotoInputHandler.photoKey(tmpField.Entity, tmpField.IDRecord)];
		this.setStatus(pInputHTMLID);
		this.setValue(pInputHTMLID, 0);
		this.showPhoto(pInputHTMLID, 0, false);
	}

	/**
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The field's value.
	 * @param {Object} [pView] - The form view, to find the record's ID.
	 */
	handleInputValue(pInput, pValue, pView)
	{
		const tmpInputHTMLID = pInput.Macro.RawHTMLID;
		this.rememberField(pInput, pView);
		const tmpHidden = this._element(this.getInputHTMLID(tmpInputHTMLID));
		const tmpIDObservation = ObservationPhotoInputHandler.idObservationOf(pValue);
		if (tmpHidden instanceof HTMLInputElement)
		{
			tmpHidden.value = String(tmpIDObservation);
		}
		this.showPhoto(tmpInputHTMLID, tmpIDObservation, !!(pInput.PictForm && pInput.PictForm.ReadOnly));
	}

	/**
	 * A freshly opened form starts with no held image, so one chosen on an abandoned form is never uploaded.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpField = this.rememberField(pInput, pView);
		delete this._pendingPhotos[ObservationPhotoInputHandler.photoKey(tmpField.Entity, tmpField.IDRecord)];
		this.handleInputValue(pInput, pValue, pView);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, pView);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}
}

module.exports = ObservationPhotoInputHandler;
